import React, { useState, useCallback, useEffect } from 'react';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import Styles from './style.module.scss';
import FileUploader from '../fileUploader/FileUploader';
import { useTranslation } from 'react-i18next';
import ICommonFormProps from '../../dynamicForms/interfaces/ICommonFormProps';
import FormElement from '../../dynamicForms/FormElement';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        delete: {
            margin: theme.spacing(1),
            color: '#c1cae1',
        },
    }),
);

interface IFormImageManipulatorProps extends ICommonFormProps {
    required: boolean;
    crop: Crop;
    hasDelete?: boolean;
    maxWidth?: number;
}

const FormImageManipulator = (props: IFormImageManipulatorProps) => {
    const [imageURL, setImageURL] = useState<string>('');
    const [crop, setCrop] = useState<Crop>();
    const [imageRef, setImageRef] = useState();
    const [isCompleted, setIsCompleted] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const { t } = useTranslation();
    const classes = useStyles();
    const maxWidth = props.formProps?.maxWidth;

    useEffect(() => {
        if (!isCompleted && imageURL.length > 0) {
            setCrop({
                unit: '%',
                height: 50,
                width: 50,
                x: 0,
                y: 0,
                ...props.formProps?.crop,
            });
        }
        //eslint-disable-next-line
    }, [imageURL, props.formProps?.crop]);
    useEffect(() => {
        //Check if its a file type
        if (props.value !== undefined) {
            if (typeof props.value?.name === 'string') {
                selectFile('', props.value);
                setIsCompleted(true);
                setIsDisabled(true);
            } else if (typeof props.value === 'string' && props.value.length) {
                setImageURL(props.value + '?query=' + Math.random());
                setIsCompleted(true);
                setIsDisabled(true);
            }
        }
        //eslint-disable-next-line
    }, [props.value]);
    useEffect(() => {
        /**selects the first crop obj going onto canvas */
        if (imageRef !== undefined && crop !== undefined && isCompleted === false) {
            createCutOfImage(imageRef, crop);
        }
        // eslint-disable-next-line
    }, [imageRef]);
    const onChange = (newCrop: Crop) => {
        if (props.formProps?.onChange) {
            props.formProps?.onChange();
        }

        setCrop(newCrop);
    };

    const selectFile = async (key: string, file: File) => {
        if (file) {
            if (props.formProps?.onValueChange) {
                props.formProps?.onValueChange(true);
            }
            if (isDisabled) {
                setIsDisabled(false);
            }

            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setImageURL(reader.result as string);
            });
            reader.readAsDataURL(file);
            if (isCompleted) {
                setIsCompleted(false);
            }
        }
    };
    const onLoad = useCallback((img) => {
        setImageRef(img);
    }, []);

    const onClickButton = () => {
        if (imageRef !== undefined && crop?.width !== undefined && crop?.height !== undefined) {
            createCutOfImage(imageRef, crop);
        }
    };
    const onRemoveFile = () => {
        setImageRef(undefined);
        setImageURL('');
        setIsCompleted(false);
    };
    const getCroppedWidth = (cropObj: any, img: any) => {
        if (!maxWidth) return cropObj.width;
        const scaleWidth = cropObj.width / img.width;

        return img.naturalWidth * scaleWidth < maxWidth ? img.naturalWidth * scaleWidth : maxWidth;
    };
    const getCroppedHeight = (cropObj: any, img: any) => {
        if (!maxWidth) return cropObj.height;

        const scaleWidth = cropObj.width / img.width;
        const scaleHeight = cropObj.height / img.height;

        const scaleHeightBasedOnMaxWidth = maxWidth / (img.naturalWidth * scaleWidth);

        return img.naturalWidth * scaleWidth < maxWidth
            ? img.naturalHeight * scaleHeight
            : img.naturalHeight * scaleHeight * scaleHeightBasedOnMaxWidth;
    };
    const createCutOfImage = (img: any, cropObj: any) => {
        if (cropObj === undefined || img === undefined) {
            return;
        }
        const fakeCanvas = document.createElement('canvas');
        let scaleX;
        let scaleY;
        if (img.naturalWidth && img.width) {
            scaleX = img.naturalWidth / img.width;
        }
        if (img.naturalHeight && img.height) {
            scaleY = img.naturalHeight / img.height;
        }
        if (scaleX === undefined || scaleY === undefined) {
            return;
        }
        const croppedWidth = getCroppedWidth(cropObj, img);
        const croppedHeight = getCroppedHeight(cropObj, img);
        fakeCanvas.width = generateCanvasNumbers(croppedWidth);
        fakeCanvas.height = generateCanvasNumbers(croppedHeight);
        const ctx = fakeCanvas.getContext('2d');
        if (ctx === null) {
            return;
        }
        ctx.drawImage(
            img,
            Math.round(cropObj.x * scaleX),
            Math.round(cropObj.y * scaleY),
            Math.round(cropObj.width * scaleX),
            Math.round(cropObj.height * scaleY),
            0,
            0,
            croppedWidth,
            croppedHeight,
        );
        return new Promise((resolve, reject) => {
            fakeCanvas.toBlob((blob) => {
                if (blob === null) {
                    return;
                }
                const file = new File([blob], props.keyName + '.png', {
                    type: 'image/png',
                });
                if (props.setValue) {
                    props.setValue(props.keyName, file, { shouldDirty: true });
                }
                setIsCompleted(true);
            }, 'image/png');
        });
    };

    const handleDelete = () => {
        if (props.setValue) {
            props.setValue(props.keyName, 'delete', { shouldDirty: true });
        }

        setImageURL('');

        if (props.formProps?.onValueChange) {
            props.formProps?.onValueChange(true);
        }
    };

    return (
        <FormElement {...props}>
            <div
                className={`${Styles.imageManipulator} ${Styles.StylishUploader}`}
                data-component-name="IMAGE_MANIPULATOR"
            >
                <FileUploader
                    {...props}
                    typeOfFile={props.formProps?.typeOfFile || 'img'}
                    onSelect={selectFile}
                    required={props.required}
                    onDeSelect={onRemoveFile}
                    disabled={props.disabled}
                    onFailedInteraction={props?.formProps?.onFailedInteraction}
                />

                <div>
                    {props.formProps?.hasDelete && imageURL && (
                        <IconButton className={classes.delete} onClick={handleDelete} disabled={props.disabled}>
                            <DeleteIcon fontSize="large" />
                        </IconButton>
                    )}
                </div>

                <div>
                    {imageURL && (
                        <ReactCrop
                            src={imageURL}
                            onImageLoaded={onLoad}
                            crop={crop}
                            onChange={onChange}
                            disabled={isDisabled}
                            onComplete={onClickButton}
                        />
                    )}
                </div>

                {imageURL && !isCompleted && props.required && <p>{t('errors.formerrors.imageman')}</p>}
            </div>
        </FormElement>
    );
};

/**
 * Fix for firefox
 * https://stackoverflow.com/questions/19338032/canvas-indexsizeerror-index-or-size-is-negative-or-greater-than-the-allowed-a
 */
function generateCanvasNumbers(number: number) {
    return Math.max(1, Math.floor(number));
}

export default FormImageManipulator;
